/* eslint-disable @typescript-eslint/naming-convention */
import moment, { Moment } from 'moment';
import {
    get as _get,
    map as _map,
    chunk as _chunk,
    concat as _concat,
    flatten as _flatten,
    isEmpty as _isEmpty,
    toString as _toString,
} from 'lodash';

import { Injectable, inject } from '@angular/core';
import { Observable, forkJoin, of } from 'rxjs';
import { map, mergeMap, take } from 'rxjs/operators';
import { AjaxResponse } from 'rxjs/ajax';

import { environment } from 'src/environments/environment';
import { BaseRequester } from '../common/base-requester';
import { unwrapLsiEnvelope } from '../common/lsi-envelope';
import { retryOnThrottle } from '../common/throttle-retry';
import { OrderStatus } from './enums/order-status';
import {
    LazadaAuthInfo,
    LazadaDelegateRequest,
} from './interfaces/lazada-delegate-request';
import { DocType } from './enums/doc-type';
import { LazadaOrderFacts, parseAmount } from '../finance/normalize-lazada';

// The rows of a Lazada finance response, whichever way Lazada nests them.
//
// requestLazadaDelegate already strips the `{code, data}` envelope, so `data`
// here is what Lazada put under `data`. For the finance endpoints that is the
// row array itself today, but when the finance sync was written (2026-08-05)
// it was one level deeper, `{data: [...]}`, and the code read it there. When
// Lazada flattened it, every finance call quietly returned an empty page —
// no error, no warning — and Lazada stopped syncing on 6 August 2026 with the
// Verify panel reporting "platform: 0" for both stores.
//
// Both shapes are accepted, and anything else is an error rather than an
// empty month: an empty month is `[]`, never "no array anywhere".
const lazadaRows = (data: unknown, servicePath: string): unknown[] => {
    if (Array.isArray(data)) {
        return data;
    }
    const nested = _get(data, 'data');
    if (Array.isArray(nested)) {
        return nested;
    }
    throw new Error(
        `${servicePath} returned no rows array (keys: ${Object.keys(
            (data as object) ?? {},
        ).join(', ')})`,
    );
};

const LAZADA_DATETIME_FORMAT = 'YYYY-MM-DD HH:mm:ss';
const REVERSE_ORDERS_PAGE_SIZE = 100;
// Lazada's documented maximum for /orders/get.
const ORDERS_PAGE_SIZE = 100;

@Injectable({
    providedIn: 'root',
})
export class LazadaOpenService extends BaseRequester {
    private lazadaAuthInfo = inject<LazadaAuthInfo>('lazadaAuthInfo' as any);

    private delegateEndpoint = environment.lazadaDelegateEndPoint;

    constructor() {
        super();
    }

    getOrderByStatus(orderStatus: OrderStatus): Observable<unknown> {
        const request: LazadaDelegateRequest = {
            authInfo: this.lazadaAuthInfo,
            servicePath: '/orders/get',
            parameter: {
                status: orderStatus,
                created_after: moment().subtract(3, 'months').format(),
            },
        };
        return this.requestLazadaDelegate(request);
    }

    // A single order by its order id — the value the console prints as the
    // order number. Resolves to Lazada's order object (price, voucher,
    // shipping_fee, address_billing, created_at, ...) without its items; pair
    // with getOrderItems for the lines.
    getOrder(orderId: string): Observable<any> {
        const request: LazadaDelegateRequest = {
            authInfo: this.lazadaAuthInfo,
            servicePath: '/order/get',
            parameter: {
                order_id: orderId,
            },
        };
        return this.requestLazadaDelegate(request);
    }

    // Ids are accepted as strings too: a 16-digit Lazada order id sits close
    // to Number.MAX_SAFE_INTEGER, and the request serialises them verbatim
    // either way.
    getOrderItems(orderIds: (number | string)[]): Observable<any[]> {
        if (_isEmpty(orderIds)) {
            return of([]);
        }
        const orderIdChunks: (number | string)[][] = _chunk(orderIds, 50);
        const requestObservers: Observable<any>[] = _map(
            orderIdChunks,
            (_orderIds: (number | string)[]) => {
                return this.requestLazadaDelegate({
                    authInfo: this.lazadaAuthInfo,
                    servicePath: '/orders/items/get',
                    parameter: {
                        order_ids: `[${_orderIds.toString()}]`,
                    },
                });
            },
        );
        return forkJoin(requestObservers).pipe(
            map((responses: any[]) => {
                return _flatten(responses);
            }, take(1)),
        );
    }

    setInvoiceNumber(orderItemId: number): Observable<unknown> {
        const request: LazadaDelegateRequest = {
            authInfo: this.lazadaAuthInfo,
            method: 'post',
            servicePath: '/order/invoice_number/set',
            parameter: {
                order_item_id: orderItemId,
                invoice_number: `A${orderItemId}`,
            },
        };
        return this.requestLazadaDelegate(request);
    }

    setStatusToPackedByMarketplace(
        orderItemIds: number[],
    ): Observable<unknown> {
        const request: LazadaDelegateRequest = {
            authInfo: this.lazadaAuthInfo,
            method: 'post',
            servicePath: '/order/pack',
            parameter: {
                order_item_ids: `[${orderItemIds.toString()}]`,
                shipping_provider:
                    'Pickup: Flash Express, Delivery: Flash Express',
                delivery_type: 'dropship',
            },
        };
        return this.requestLazadaDelegate(request);
    }

    setStatusToReadyToShip(
        orderItemIds: number[],
        shipmentProvider: string,
        trackingCode: string,
    ): Observable<unknown> {
        const request: LazadaDelegateRequest = {
            authInfo: this.lazadaAuthInfo,
            method: 'post',
            servicePath: '/order/rts',
            parameter: {
                order_item_ids: `[${orderItemIds.toString()}]`,
                delivery_type: 'dropship',
                shipment_provider: shipmentProvider,
                tracking_number: trackingCode,
            },
        };
        return this.requestLazadaDelegate(request);
    }

    getDocument(docType: DocType, orderItemIds: number[]): Observable<unknown> {
        const request: LazadaDelegateRequest = {
            authInfo: this.lazadaAuthInfo,
            method: 'get',
            servicePath: '/order/document/get',
            parameter: {
                doc_type: docType,
                order_item_ids: `[${orderItemIds.toString()}]`,
            },
        };
        return this.requestLazadaDelegate(request);
    }

    // --- Finance -----------------------------------------------------------

    // Every financial movement in the window: one row per fee per order item,
    // plus one "Item Price Credit" row per item for the sale itself.
    // normalize-lazada.ts pivots these back into one record per order.
    //
    // ⚠️ The path is /finance/transaction/details/get — PLURAL. The singular
    // form also resolves and returns InsufficientPermission ("App does not
    // have permission to access this api") regardless of what is actually
    // granted in Lazada Open Platform, which reads exactly like a missing
    // permission and is not one. Verified against both stores 2026-08-05.
    //
    // Dates are 'YYYY-MM-DD'. 500 rows per page; paged by offset until a short
    // page comes back.
    getFinanceTransactions(
        startDate: string,
        endDate: string,
        offset: number = 0,
        accumItem?: unknown[],
    ): Observable<unknown[]> {
        const pageSize = 500;
        const request: LazadaDelegateRequest = {
            authInfo: this.lazadaAuthInfo,
            servicePath: '/finance/transaction/details/get',
            parameter: {
                start_time: startDate,
                end_time: endDate,
                limit: pageSize,
                offset,
            },
        };
        // Retry this page only — see the note in throttle-retry.ts on why a
        // retry around the recursion is worse than none.
        return this.requestLazadaDelegate(request).pipe(
            retryOnThrottle(),
            mergeMap((data: any) => {
                const page = lazadaRows(data, request.servicePath);
                const accumulated = _concat(accumItem || [], page);
                // A full page means there is probably another. A short page is
                // the end — Lazada gives no total count to page against.
                if (page.length === pageSize) {
                    return this.getFinanceTransactions(
                        startDate,
                        endDate,
                        offset + pageSize,
                        accumulated,
                    );
                }
                return of(accumulated);
            }),
        );
    }

    // order_number -> what the finance ledger does not know about the order:
    // created_at (epoch seconds) and how its discount was funded. The ledger
    // carries no order date — only transaction_date, which is when Lazada
    // credited the account, typically after delivery — so this is the only
    // way to date a Lazada sale at its tax point; and it states Item Price
    // Credit without saying how much of it Lazada's own voucher paid for.
    //
    // The caller must widen the window well before the reporting period: an
    // order settling in July was created earlier, sometimes much earlier.
    getOrderFacts(
        createdAfterIso: string,
        createdBeforeIso: string,
        offset: number = 0,
        accum?: Record<string, LazadaOrderFacts>,
    ): Observable<Record<string, LazadaOrderFacts>> {
        const pageSize = 100;
        const request: LazadaDelegateRequest = {
            authInfo: this.lazadaAuthInfo,
            servicePath: '/orders/get',
            parameter: {
                created_after: createdAfterIso,
                created_before: createdBeforeIso,
                limit: pageSize,
                offset,
                sort_by: 'created_at',
                sort_direction: 'ASC',
            },
        };
        return this.requestLazadaDelegate(request).pipe(
            retryOnThrottle(),
            mergeMap((data: any) => {
                const orders: any[] = _get(data, 'orders') || [];
                const merged: Record<string, LazadaOrderFacts> = {
                    ...(accum || {}),
                };
                for (const order of orders) {
                    // Join on order_number: that is what the finance ledger
                    // calls order_no. order_id is a different, shorter value.
                    const key = _toString(
                        _get(order, 'order_number') ?? _get(order, 'order_id'),
                    );
                    if (!key) {
                        continue;
                    }
                    // Strict, with the format pinned. Lazada returns
                    // "2026-07-24 14:02:56 +0700", which is neither ISO-8601
                    // nor RFC-2822, so moment's forgiving parser falls back to
                    // `new Date()` — behaviour it documents as unreliable
                    // across browsers, and this value decides which month a
                    // sale is taxed in. The offset is part of the string and
                    // is honoured, so the result is correct regardless of the
                    // browser's own timezone.
                    const created = moment(
                        _toString(_get(order, 'created_at')),
                        'YYYY-MM-DD HH:mm:ss Z',
                        true,
                    );
                    merged[key] = {
                        createdAt: created.isValid() ? created.unix() : null,
                        // Numbers on the live API, but parsed like the ledger
                        // strings in case that changes.
                        voucherPlatform:
                            parseAmount(_get(order, 'voucher_platform')) ?? 0,
                        voucherSeller:
                            parseAmount(_get(order, 'voucher_seller')) ?? 0,
                    };
                }
                if (orders.length === pageSize) {
                    return this.getOrderFacts(
                        createdAfterIso,
                        createdBeforeIso,
                        offset + pageSize,
                        merged,
                    );
                }
                return of(merged);
            }),
        );
    }

    // Statement-level payouts: what actually reached the bank, and when.
    // Coarser than the transaction rows above, and used to reconcile them.
    getPayoutStatus(createdAfter: string): Observable<unknown[]> {
        const request: LazadaDelegateRequest = {
            authInfo: this.lazadaAuthInfo,
            servicePath: '/finance/payout/status/get',
            parameter: { created_after: createdAfter },
        };
        return this.requestLazadaDelegate(request).pipe(
            map((data: any) => lazadaRows(data, request.servicePath)),
        );
    }

    // Every order in `orderStatus` created at or after `createdAfter`,
    // walking limit/offset to the end. getOrderByStatus above sends no
    // limit and takes whatever single page Lazada chooses to return.
    getAllOrdersByStatus(
        orderStatus: OrderStatus,
        createdAfter: Moment,
        offset: number = 0,
        accumItem?: any[],
    ): Observable<any[]> {
        const request: LazadaDelegateRequest = {
            authInfo: this.lazadaAuthInfo,
            servicePath: '/orders/get',
            parameter: {
                status: orderStatus,
                created_after: createdAfter.format(),
                limit: ORDERS_PAGE_SIZE,
                offset,
            },
        };
        return this.requestLazadaDelegate(request).pipe(
            retryOnThrottle(),
            mergeMap((data: any) => {
                const page: any[] = _get(data, 'orders') || [];
                const accumulated = _concat(accumItem || [], page);
                const count = Number(_get(data, 'count'));
                const countReached =
                    Number.isFinite(count) &&
                    count > 0 &&
                    accumulated.length >= count;
                if (page.length === ORDERS_PAGE_SIZE && !countReached) {
                    return this.getAllOrdersByStatus(
                        orderStatus,
                        createdAfter,
                        offset + ORDERS_PAGE_SIZE,
                        accumulated,
                    );
                }
                return of(accumulated);
            }),
        );
    }

    // --- Returns -----------------------------------------------------------

    // Reverse (return / refund) orders, 100 per page, for trade orders
    // created inside the window.
    //
    // The window is on the ORIGINAL order's creation date — Lazada offers no
    // filter on when the return was opened — so a caller after "returns
    // opened in the last N days" must start the window earlier by however
    // long after purchase a return can still be opened. Dates go as
    // 'YYYY-MM-DD HH:mm:ss' in the seller's local time (verified 2026-09-22).
    //
    // ⚠️ On 2026-09-23 the window did not narrow anything: every call came
    // back with the store's whole history (343 rows on lazada-genlogic)
    // whatever dates it named. Callers must filter the rows themselves.
    // Failed deliveries are never listed here at all — they are orders in
    // status failed / shipped_back (getAllOrdersByStatus).
    getReverseOrders(
        orderCreatedFrom: Moment,
        orderCreatedTo: Moment,
        pageNo: number = 1,
        accumItem?: any[],
    ): Observable<any[]> {
        const request: LazadaDelegateRequest = {
            authInfo: this.lazadaAuthInfo,
            servicePath: '/reverse/getreverseordersforseller',
            parameter: {
                trade_order_gmt_create_start: orderCreatedFrom.format(
                    LAZADA_DATETIME_FORMAT,
                ),
                trade_order_gmt_create_end: orderCreatedTo.format(
                    LAZADA_DATETIME_FORMAT,
                ),
                page_size: REVERSE_ORDERS_PAGE_SIZE,
                page_no: pageNo,
            },
        };
        // Unlike every order endpoint this one answers { code, result } —
        // there is no `data` — and `result` carries its own success flag.
        return this.requestLazadaDelegate(request, 'result').pipe(
            retryOnThrottle(),
            mergeMap((result: any) => {
                if (_get(result, 'success') === false) {
                    throw new Error(
                        `${request.servicePath}: ${_toString(
                            _get(result, 'error_msg') ||
                                _get(result, 'error_code') ||
                                'success=false',
                        )}`,
                    );
                }
                const page: any[] = _get(result, 'items') || [];
                const accumulated = _concat(accumItem || [], page);
                const total = Number(_get(result, 'total'));
                // A short page is the last one whatever `total` says. A full
                // page means another unless `total` is known and already
                // reached — a missing or zero `total` must not end the walk
                // after page one.
                const totalReached =
                    Number.isFinite(total) &&
                    total > 0 &&
                    accumulated.length >= total;
                if (page.length === REVERSE_ORDERS_PAGE_SIZE && !totalReached) {
                    return this.getReverseOrders(
                        orderCreatedFrom,
                        orderCreatedTo,
                        pageNo + 1,
                        accumulated,
                    );
                }
                return of(accumulated);
            }),
        );
    }

    // `rootField` names where the endpoint puts its payload: `data` for the
    // order, product and finance endpoints, `result` for the reverse-order
    // one.
    private requestLazadaDelegate(
        request: LazadaDelegateRequest,
        rootField: 'data' | 'result' = 'data',
    ): Observable<unknown> {
        return this.internalPostRequest<LazadaDelegateRequest>(
            this.delegateEndpoint,
            request,
        ).pipe(
            map((ajaxRes: AjaxResponse) => {
                const body = unwrapLsiEnvelope(_get(ajaxRes, 'response'));
                const responseCodeString: string = _toString(
                    _get(body, 'code'),
                );
                if (responseCodeString === '0') {
                    return _get(body, rootField);
                } else {
                    throw new Error(
                        `[${_get(body, 'code')}] ${_get(body, 'message')}`,
                    );
                }
            }, take(1)),
        );
    }
}
